import type { VaultNote } from "./types.js";

/**
 * Keeps a real selection when possible and otherwise chooses the first note in
 * the current list. This prevents a populated offline cache from rendering the
 * empty-vault welcome after a stale remembered id or a trip to All notes.
 */
export function vaultSelection(
  notes: readonly VaultNote[],
  selectedId: string,
  visible: readonly VaultNote[] = notes,
): string {
  if (notes.some(({ id }) => id === selectedId)) return selectedId;
  return visible[0]?.id ?? notes[0]?.id ?? "";
}

export function extractWikilinks(body: string): readonly string[] {
  return [...body.matchAll(/\[\[([^\]|#]+)(?:#[^\]|]+)?(?:\|[^\]]+)?\]\]/g)]
    .map((match) => match[1]?.trim() ?? "")
    .filter(Boolean);
}

export function exactSearch(notes: readonly VaultNote[], query: string): readonly VaultNote[] {
  const terms = query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [...notes];
  return notes
    .map((note) => {
      const title = `${note.title} ${note.path}`.toLocaleLowerCase();
      const body = note.body.toLocaleLowerCase();
      const score = terms.reduce((sum, term) => {
        if (title.includes(term)) return sum + 5;
        if (body.includes(term)) return sum + 1;
        return sum - 100;
      }, 0);
      return { note, score };
    })
    .filter(({ score }) => score >= 0)
    .sort((a, b) => b.score - a.score || b.note.updatedAt.localeCompare(a.note.updatedAt))
    .map(({ note }) => note);
}

export function backlinks(notes: readonly VaultNote[], target: VaultNote): readonly VaultNote[] {
  const candidates = new Set([
    target.title.toLocaleLowerCase(),
    target.path.replace(/\.md$/i, "").toLocaleLowerCase(),
  ]);
  return notes.filter((note) =>
    note.id !== target.id && extractWikilinks(note.body).some((link) => candidates.has(link.toLocaleLowerCase())),
  );
}

export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export function renderMarkdown(markdown: string): string {
  const escaped = escapeHtml(markdown);
  return escaped
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>")
    .replace(/^&gt; (.+)$/gm, "<blockquote>$1</blockquote>")
    .replace(/^- \[x\] (.+)$/gim, '<p class="task done">✓ $1</p>')
    .replace(/^- \[ \] (.+)$/gim, '<p class="task">○ $1</p>')
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g, (_whole, target: string, label?: string) =>
      `<button class="wikilink" data-wikilink="${target.trim()}">${label?.trim() || target.trim()}</button>`,
    )
    .split(/\n{2,}/)
    .map((block) => /^<(?:h\d|blockquote|p)/.test(block) ? block : `<p>${block.replaceAll("\n", "<br>")}</p>`)
    .join("");
}

export function makeId(prefix = "rv"): string {
  return `${prefix}_${crypto.randomUUID()}`;
}

export async function makeEtag(body: string): Promise<string> {
  const bytes = new TextEncoder().encode(body);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}
